import { useEffect, useMemo, useState } from "react";
import {
  createRecurringItem,
  deleteRecurringItem,
  fetchCompanies,
  fetchRecurringItems,
  patchRecurringItem,
  type Company,
  type RecurringFrequency,
  type RecurringItem,
} from "../api";
import { formatDateEs, formatMoney } from "../lib/grouping";

const FREQ_LABEL: Record<RecurringFrequency, string> = {
  monthly: "Mensual",
  quarterly: "Trimestral",
  annual: "Anual",
  once: "Una vez",
};

type DraftItem = {
  companyId: string;
  name: string;
  kind: "inflow" | "outflow";
  amount: string;
  currency: string;
  frequency: RecurringFrequency;
  dayOfMonth: string;
  startDate: string;
  endDate: string;
  accountIban: string;
  notes: string;
};

function emptyDraft(): DraftItem {
  const today = new Date().toISOString().slice(0, 10);
  return {
    companyId: "",
    name: "",
    kind: "outflow",
    amount: "",
    currency: "EUR",
    frequency: "monthly",
    dayOfMonth: "",
    startDate: today,
    endDate: "",
    accountIban: "",
    notes: "",
  };
}

export function RecurringItems() {
  const [items, setItems] = useState<RecurringItem[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<DraftItem>(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [filterCompany, setFilterCompany] = useState<string>("");

  const load = async () => {
    setError(null);
    try {
      const [r, c] = await Promise.all([
        fetchRecurringItems(filterCompany || undefined),
        fetchCompanies(),
      ]);
      setItems(r.items);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();

  }, [filterCompany]);

  const companyName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of companies) m.set(c.id, c.alias || c.name);
    return m;
  }, [companies]);

  const save = async () => {
    if (!draft.companyId) return alert("Selecciona una empresa");
    if (!draft.name.trim()) return alert("Indica un nombre para el concepto");
    const amount = Number(draft.amount.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) return alert("Importe inválido");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.startDate)) return alert("Fecha inicio inválida");
    if (draft.endDate && !/^\d{4}-\d{2}-\d{2}$/.test(draft.endDate)) return alert("Fecha fin inválida");
    const dayRaw = draft.dayOfMonth.trim();
    let dayOfMonth: number | null = null;
    if (dayRaw) {
      const d = Number(dayRaw);
      if (!Number.isInteger(d) || d < 1 || d > 28) return alert("Día del mes: 1-28");
      dayOfMonth = d;
    }
    setBusy(true);
    try {
      await createRecurringItem({
        companyId: draft.companyId,
        name: draft.name.trim(),
        kind: draft.kind,
        amount,
        currency: draft.currency || "EUR",
        frequency: draft.frequency,
        dayOfMonth,
        startDate: draft.startDate,
        endDate: draft.endDate || null,
        accountIban: draft.accountIban.replace(/\s+/g, "").toUpperCase() || null,
        notes: draft.notes.trim() || undefined,
      });
      setDraft(emptyDraft());
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Conceptos recurrentes</h2>
          <p className="meta">
            Nóminas, alquileres, cuotas de leasing, impuestos periódicos y demás
            entradas/salidas de caja que <b>no</b> aparecen como partida abierta
            en SAP. La previsión de tesorería los expande a instancias por fecha
            y las suma a cada periodo.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
            <option value="">— Todas las empresas —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.alias || c.name} ({c.id})</option>
            ))}
          </select>
          <button type="button" onClick={() => setCreating((x) => !x)} disabled={companies.length === 0}>
            {creating ? "✕ Cancelar" : "+ Nuevo concepto"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {companies.length === 0 && (
        <p className="warning">
          No hay empresas. Crea al menos una en "Empresas" para asociar recurrentes.
        </p>
      )}

      {creating && companies.length > 0 && (
        <div className="form-row" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
          <select value={draft.companyId} onChange={(e) => setDraft({ ...draft, companyId: e.target.value })}>
            <option value="">— Empresa —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.alias || c.name} ({c.id})</option>
            ))}
          </select>
          <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as "inflow" | "outflow" })}>
            <option value="outflow">Pago (salida)</option>
            <option value="inflow">Cobro (entrada)</option>
          </select>
          <input
            placeholder="Nombre (ej. Nómina)"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            style={{ minWidth: "12rem" }}
          />
          <input
            placeholder="Importe"
            value={draft.amount}
            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            style={{ width: "8rem" }}
          />
          <input
            placeholder="EUR"
            value={draft.currency}
            onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
            style={{ width: "5rem" }}
          />
          <select value={draft.frequency} onChange={(e) => setDraft({ ...draft, frequency: e.target.value as RecurringFrequency })}>
            {(Object.keys(FREQ_LABEL) as RecurringFrequency[]).map((f) => (
              <option key={f} value={f}>{FREQ_LABEL[f]}</option>
            ))}
          </select>
          <input
            placeholder="Día 1-28 (opc.)"
            value={draft.dayOfMonth}
            onChange={(e) => setDraft({ ...draft, dayOfMonth: e.target.value })}
            style={{ width: "8rem" }}
          />
          <label style={{ display: "flex", flexDirection: "column", fontSize: "0.75rem" }}>
            Inicio
            <input type="date" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", fontSize: "0.75rem" }}>
            Fin (opc.)
            <input type="date" value={draft.endDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
          </label>
          <input
            placeholder="IBAN (opc.)"
            value={draft.accountIban}
            onChange={(e) => setDraft({ ...draft, accountIban: e.target.value })}
            style={{ minWidth: "16rem" }}
          />
          <input
            placeholder="Notas"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            style={{ minWidth: "12rem" }}
          />
          <button type="button" onClick={save} disabled={busy}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && <p className="meta">No hay conceptos recurrentes dados de alta.</p>}
      {items && items.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>Concepto</th>
              <th>Tipo</th>
              <th className="num">Importe</th>
              <th>Frecuencia</th>
              <th>Día</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Activo</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <RecurringRow
                key={it.id}
                item={it}
                companyLabel={companyName.get(it.companyId) ?? it.companyId}
                onChanged={load}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RecurringRow(props: {
  item: RecurringItem;
  companyLabel: string;
  onChanged: () => void;
}) {
  const { item, companyLabel, onChanged } = props;
  const [busy, setBusy] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await patchRecurringItem(item.id, { active: !item.active });
      onChanged();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(`¿Eliminar "${item.name}"?`)) return;
    setBusy(true);
    try {
      await deleteRecurringItem(item.id);
      onChanged();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = item.kind === "inflow" ? "Cobro" : "Pago";
  const kindColor = item.kind === "inflow" ? "#2f9e44" : "#c92a2a";
  const signed = item.kind === "inflow" ? item.amount : -item.amount;

  return (
    <tr style={{ opacity: item.active ? 1 : 0.55 }}>
      <td>{companyLabel}</td>
      <td>
        <div style={{ fontWeight: 500 }}>{item.name}</div>
        {item.notes && <div className="meta">{item.notes}</div>}
      </td>
      <td style={{ color: kindColor, fontWeight: 500 }}>{kindLabel}</td>
      <td className="num">{formatMoney(signed, item.currency)}</td>
      <td>{FREQ_LABEL[item.frequency]}</td>
      <td>{item.dayOfMonth ?? "—"}</td>
      <td>{formatDateEs(item.startDate)}</td>
      <td>{item.endDate ? formatDateEs(item.endDate) : "—"}</td>
      <td>
        <button type="button" onClick={toggleActive} disabled={busy}>
          {item.active ? "Sí" : "No"}
        </button>
      </td>
      <td>
        <button type="button" onClick={remove} disabled={busy}>Eliminar</button>
      </td>
    </tr>
  );
}
